import { useEffect } from 'react';
import logger from 'ws-scripts/modules/logger';

// Remove multiple form fields by their name attributes
const removeFields = (form, fieldNames) => {
	fieldNames.forEach((name) =>
		form.find(`[name="${name}"]`).closest('.form-group').remove()
	);
};

// Build the hidden comments string from form values + event data
const buildCommentString = (
	event,
	numberAttendees,
	currentOwner,
	contactOptin
) => {
	return [
		'[DDC Event Lead',
		`DMSId:${event.DMSId || ''}`,
		`AutoLoopId:${event.ILMId || ''}`,
		`EventID:${event.id || ''}`,
		`DealerEventName:${event.name || ''}`,
		`NationEventName:${event.nationalEventName || ''}`,
		`NumberOfAttendees:${numberAttendees}`,
		`CurrentOwner:${currentOwner}`,
		`ContactOptIn:${contactOptin}]`
	].join('|');
};

// Alert message content
const insertAlertMessage = (message) => {
	if (!message) return null;
	return $('<div>', {
		class: 'alert-info alert',
		html: `<i class="ddc-icon ddc-icon-alert align-left"></i> ${$('<div>')
			.text(message)
			.html()}`
	});
};

/**
 * Custom hook to initialize and manage the event RSVP form with jQuery
 * @param {Object} event - Event object with RSVP and restriction info
 * @param {Object} labels - The labels object containing localized strings
 * @returns {boolean} Whether the form was successfully initialized
 */
export const useEventRsvpForm = (event, labels) => {
	// Use a ref to track if initialization was successful
	const formSelector = '.event-rsvp';

	useEffect(() => {
		// Initialize the form when the component mounts or when dependencies change
		const eventRsvpForm = $(formSelector);

		if (eventRsvpForm.length === 0) {
			logger.error('Event RSVP form not found');
			return null;
		}

		let alertMessage;
		// Determine RSVP / restriction states
		const isNonRsvp = event.rsvp !== true;
		const isRestricted = event.restricted === true;

		if (isNonRsvp || isRestricted) {
			// Set third party lead tag
			eventRsvpForm
				.find('[name="thirdPartyLeadTag"]')
				.val('Event Non-RSVP');

			// Remove unnecessary fields
			removeFields(eventRsvpForm, [
				'contact.address.street',
				'contact.address.street2',
				'contact.address.city',
				'contact.address.state',
				'numberofAttendees'
			]);

			// Handle specific messaging
			if (isNonRsvp) {
				eventRsvpForm.find('h3').text(labels.get('CONTACT_US'));
				alertMessage = insertAlertMessage(event.nonRsvpMsg);
			} else if (isRestricted) {
				alertMessage = insertAlertMessage(event.restrictedMsg);
			}

			if (alertMessage) {
				eventRsvpForm.find('form').before(alertMessage);
			}
		}
		// Update comments field when relevant inputs change
		eventRsvpForm.on(
			'change.rsvp',
			'[data-id="numberofAttendees"], [data-id="currentOwner"], [data-id="contactOptin"]',
			() => {
				const numberAttendees =
					eventRsvpForm.find('[data-id="numberofAttendees"]').val() ||
					'';
				const currentOwner = (
					eventRsvpForm.find('[data-id="currentOwner"]').val() || ''
				).replace('No', 'false');
				const contactOptin = (
					eventRsvpForm.find('[data-id="contactOptin"]').val() || ''
				).replace('No', 'false');

				const comments = buildCommentString(
					event,
					numberAttendees,
					currentOwner,
					contactOptin
				);
				eventRsvpForm.find('[data-id="comments"]').val(comments);
				logger.info('Appended to Comments Field:', comments);
			}
		);

		// Show the form
		eventRsvpForm.removeClass('hide');

		// Clean up function that runs when the component unmounts or dependencies change
		return () => {
			// Remove event handlers to prevent memory leaks
			eventRsvpForm.off('change.rsvp');
		};
	}, [event, labels, formSelector]); // Re-run when these dependencies change
};

export default useEventRsvpForm;
